import React, {useContext, useState} from 'react';
import {TableBodyProps} from '@src/@types/components/List';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import {IFormFieldItemPropsContext} from '@src/@interfaces/contexts/IFormFieldItemPropsContext';
import {IOrderModel} from '@src/@interfaces/models/Order/IOrderModel';
import {FormFieldItemPropsContext} from '@src/contexts/FormFieldItemPropsContext';
import classnames from '@src/helpers/classnames';
import {IOrderPaymentModel} from '@src/@interfaces/models/Order/IOrderPaymentModel';
import Row from '@views/orders/fields/payments/extra-row/Row';
import {FormFieldOption} from '@src/@types/components/Form';
import {useFormContext, useWatch} from 'react-hook-form';

type Params<Model extends IEmptyModel> = Parameters<Required<TableBodyProps<Model>>['renderRow']>;

type Props<Model extends IEmptyModel> = {
    properties: Params<Model>[0],
    baseRenderer: Params<Model>[1],
    baseContentRenderer: Params<Model>[2],
    modelOptions?: Record<string, FormFieldOption[]>,
};

export default function TableRow(props: Props<IOrderPaymentModel>) {
    const {
        forceDisabled,
        forceReadonly,
    } = useContext<IFormFieldItemPropsContext<IOrderModel> | null>(FormFieldItemPropsContext) ?? {};
    const [isExpanded, setIsExpanded] = useState(props.properties.model?.id == null);
    const {control} = useFormContext();

    const selectedTypeId = useWatch({control, name: `payments.${props.properties.rowIndex}.payment_type_id`});
    const types = props.modelOptions?.paymentTypes ?? [];
    const selectedType = (
            selectedTypeId ? types.find(item => item.value === selectedTypeId) : undefined
    ) as React.ComponentProps<typeof Row>['selectedPaymentType'];

    const shouldDisplayExtraRow = Boolean(selectedType?.additional_fields?.length);

    const cellRenderer: TableBodyProps<IOrderPaymentModel>['renderCellContent'] = (props, baseRenderer) => {
        if (!shouldDisplayExtraRow || props.config.name !== 'id') {
            return baseRenderer();
        }

        return <>
            {baseRenderer()}

            <button
                    type="button"
                    className="btn btn-sm btn-outline-primary ms-2"
                    disabled={forceDisabled || forceReadonly}
                    onClick={() => {
                        setIsExpanded(was => !was);
                    }}
            >
                <i className={classnames({
                    'fa fa-chevron-up': isExpanded,
                    'fa fa-chevron-down': !isExpanded
                })}/>
            </button>
        </>;
    };

    const contentRenderer: TableBodyProps<IOrderPaymentModel>['renderRowContent'] = (properties) => {
        return <>
            {properties.cellBeforeColumnsComponent}

            {properties.renderColumns(cellRenderer)}

            {properties.renderControlsColumn()}
        </>;
    };

    if (!isExpanded || !shouldDisplayExtraRow) {
        return <>{props.baseRenderer(undefined, contentRenderer)}</>;
    }

    return <>
        {props.baseRenderer(undefined, contentRenderer)}

        <tr className="models-list__content-table__extra-row">
            <td colSpan={props.properties.context?.columnsNumber}>
                <Row
                        modelOptions={props.modelOptions}
                        isDisabled={forceDisabled}
                        isReadonly={forceReadonly}
                        {...props.properties}
                        selectedPaymentType={selectedType}
                />
            </td>
        </tr>
    </>;
}
